import React from 'react';
import { observer } from 'mobx-react';
import { computed, autorun, observable } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import { ForumUI, AppUI } from '@seedlang/state';
import UserProfileImage from 'components/user/user_profile_image';
import styled from '@emotion/styled';
import ForumCount from 'components/forum/forum_count';
import ForumRow from 'components/forum/forum_row';
import Paginator from 'components/paginator';
import { PinIcon, SeedlangIcon } from '@seedlang/icons';
import { Constants } from '@seedlang/constants';
import Filter from 'components/backend_filter/filter';
import Tag from 'components/tag';
import Spinner from 'components/spinner';
import { displayTargetTextCorrectedForLanguage } from '@seedlang/utils';
import { each, keys } from 'lodash';
import Link from 'components/link';

const Wrapper = styled.div`
  .seedlang-icon {
    border: 1px solid gray;
    border-radius: 50%;
    background: #FFF;
    svg {
      width: 50px;
    }
  }
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  margin: 0 10px 0 20px;
  font-size: 16px;
  .icon-wrapper {
    margin-right: 5px;
  }
  .icon-wrapper, .pin-icon {
    height: 14px;
    width: 14px;
  }
  svg {
    width: 14px;
    fill: #333;
  }
`;

const Date = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: #999;
  width: 220px;
`;

const TagWrapper = styled.div`
  position: absolute;
  left: 0;
  top: 0;
`;

@observer
class ForumThreadIndex extends React.Component {
  @observable loadedIndex = false;

  constructor(props) {
    super(props);
    ForumUI.onReset();
    if (this.props.forumId && this.props.loadForum) {
      ForumUI.loadForum(this.props.forumId);
    }
    this.disposer = autorun(() => {
      if (!this.loadedIndex && ForumUI.forum && !AppUI.loadingUser) {
        this.loadedIndex = true;
        this.loadThreadsForCurrentPage();
      }
    });
    AppUI.createEvent('discuss - view forum', {forum_id: this.props.params?.forumId});
  }

  componentDidUpdate(prevProps) {
    if (!this.loadedIndex) {
      return;
    }
    if (parseInt(prevProps.params?.page || 1, 10) !== this.currentPage) {
      this.loadThreadsForCurrentPage({reload: true});
    }
  }

  componentWillUnmount() {
    ForumUI.forumThreadStore.clearIndexData();
    ForumUI.forumThreadStore.clearFilter();
    this.disposer && this.disposer();
  }

  @computed get currentPage() {
    return parseInt(this.props.params?.page || 1, 10);
  }

  @computed get forumId() {
    return this.props.forumId || this.props.params.forumId;
  }

  @computed get indexFilters() {
    const filters = {forum_id: this.forumId, hidden: false};
    if (this.props.defaultFilters) {
      each(keys(this.props.defaultFilters), key => {
        filters[key] = this.props.defaultFilters[key];
      });
    }
    return filters;
  }

  @computed get showFilters() {
    return (AppUI.user.admin || AppUI.user.moderator) && ForumUI.forum.showFilters;
  }

  @computed get showBackendFilters() {
    return this.showFilters && !this.props.hideBackendFilters;
  }

  @computed get filterComponents() {
    let ary = [];
    if (!this.props.groupId) {
      ary = [
        {
          type: 'multi_select',
          name: 'tag',
          label: 'Tag',
          options: [
            {id: '~Ling-DE~', name: 'Ling-DE'},
            {id: '~Ling-ES~', name: 'Ling-ES'},
            {id: '~Ling-FR~', name: 'Ling-FR'},
            {id: '~Cust-Sup~', name: 'Cust-Sup'},
            {id: '~Marie~', name: 'Marie'},
          ],
          labelField: 'name',
          default: '',
        },
      ];
    }
    if (ForumUI.forum.highlightUnseen) {
      ary.push(
        {
          type: 'boolean',
          name: 'seen_by_admin',
          label: 'Checked',
          default: '',
        },
      );
    }
    return ary;
  }

  loadThreadsForCurrentPage = ({reload = false} = {}) => {
    ForumUI.forumThreadStore.setPage(this.currentPage);
    if (reload) {
      ForumUI.forumThreadStore.reloadIndexFiltered({ids: {forumId: this.forumId}});
    } else if (!this.showBackendFilters) {
      ForumUI.forumThreadStore.getIndex({filters: this.indexFilters, sort: this.props.defaultSort});
    }
  }

  @computed get vocabForumLanguageId() {
    return keys(Constants.VOCAB_FORUM_IDS).find(key => Constants.VOCAB_FORUM_IDS[key] === ForumUI.forumStore?.params?.ids?.forumId);
  }
  getThreadRoute = (item) => {
    let route = 'forums.forum_threads.show';
    if (AppUI.siteIsMemrev) {
      route = 'creator.forum_threads.show';
    } else if (this.props.groupId) {
      route = 'classroom.forum_threads.show';
    }
    return {name: route, params: {forumThreadId: item.id, groupId: this.props.groupId, forumId: this.forumId}};
  }

  render() {
    return (
      <Wrapper>

        {
          this.showBackendFilters &&
            <Filter
              store={ForumUI.forumThreadStore}
              defaultFilters={this.indexFilters}
              defaultSort={this.props.defaultSort || '-pinned,-last_commented_at'}
              namespace='user-index'
              filters={this.filterComponents}
            />
        }
        {
          ForumUI.forumThreadStore.hasOutstandingRequests && !this.props.hideSpinner &&
            <Spinner />
        }
        {
          ForumUI.forumThreadStore.indexData.map(item => {
            let background;
            if (ForumUI.forum.highlightUnseen && (AppUI.user.moderator || AppUI.user.admin) && item.tag) {
              background = '#f9f1dc';
            } else if (ForumUI.forum.highlightUnseen && (AppUI.user.moderator || AppUI.user.admin) && !item.seenByAdmin) {
              background = '#fdfae2';
            }
            return (
              <Link
                key={item.id}
                to={this.getThreadRoute(item)}
                style={{textDecoration: 'none', color: 'inherit'}}
              >
                <ForumRow
                  background={background}
                >
                {
                  item.tag && ForumUI.user.moderator &&
                    <TagWrapper>
                      <Tag
                        text={item.tag}
                      />
                    </TagWrapper>
                }
                {
                  !item.user && !this.props.hideLogo &&
                    <SeedlangIcon />
                }
                {
                  item.user &&
                    <UserProfileImage
                      user={item.user}
                      width={50}
                    />
                }
                <Title>
                  {
                    item.pinned &&
                     <PinIcon />
                  }
                  {displayTargetTextCorrectedForLanguage(item.titleOrTarget, this.vocabForumLanguageId || item.languageId)}
                </Title>
                <Date>
                  <div>
                    {item.lastCommentedAt.relativeTime}
                  </div>
                  {
                    !this.props.hideName &&
                      <i>
                        by {item.lastCommentName || 'Anonymous'}
                      </i>
                  }
                </Date>
                <ForumCount
                  count={AppUI.user.admin || AppUI.user.admin ? item.commentsCount : item.visibleCommentsCount}
                  showCommentIcon
                />
              </ForumRow>
              </Link>
            );
          })
        }
        {
          (ForumUI.forumThreadStore.hasIndexData || ForumUI.forumThreadStore.page > 1) &&
            <Paginator
              updateUrl
              store={ForumUI.forumThreadStore}
              ids={{forumId: this.forumId}}
              margin='20px 0'
            />
        }
      </Wrapper>
    );
  }
}

export default ErrorBoundary(ForumThreadIndex);
